import { __assign, __read, __rest } from "tslib";
import React, { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import { format as dateFnsFormat, isValid as dateFnsIsValid, parse as dateFnsParse, } from 'date-fns';
import { useInputPropsMergedWithFormFieldContext } from '../FormField/FormFieldContext/FormFieldContext';
import { extractPropsForInputComponent } from '../internal/extract-props/extractPropsForInputComponent';
import { useForkRef } from '../../utils/hooks/useForkRef';
import { DatePickerInput } from './DatePickerInput';
import { DatePickerPanel } from './DatePickerPanel';
import { DatePickerCalendar } from './DatePickerCalendar';
import { getFormat, getMaxDate, getMinDate } from './utils';
import { SRoot } from './DatePicker.styled';
import { useDatePickerState } from './useDatePickerState';
import { useVelocityContext } from '../../contexts/VelocityContext/VelocityContext';
import { useCompositeComponentFocusHandlers } from '../internal/useCompositeComponentFocusHandlers/useCompositeComponentFocusHandlers';
export var DatePicker = forwardRef(function DatePicker(incomingProps, ref) {
    var _a;
    var props = useInputPropsMergedWithFormFieldContext(incomingProps);
    var _b = extractPropsForInputComponent(props), _c = _b.rootElementAttributes, inputId = _c.id, rootElementAttributes = __rest(_c, ["id"]), baseInputComponentProps = _b.baseInputComponentProps, baseHtmlInputAttributes = _b.baseHtmlInputAttributes, ariaAttributes = _b.ariaAttributes, dataAttributes = _b.dataAttributes, restProps = _b.restProps;
    var onFocus = restProps.onFocus, onBlur = restProps.onBlur, name = restProps.name, disablePast = restProps.disablePast, disableFuture = restProps.disableFuture, _d = restProps.disableWeekend, disableWeekend = _d === void 0 ? false : _d, shouldDisableDate = restProps.shouldDisableDate, _e = restProps.initialOpen, initialOpen = _e === void 0 ? false : _e, _f = restProps.inline, inline = _f === void 0 ? false : _f, autoComplete = restProps.autoComplete;
    var locale = useVelocityContext().dateFnsLocale;
    var format = getFormat(restProps.format, locale);
    var maxDate = getMaxDate(restProps.maxDate, disableFuture);
    var minDate = getMinDate(restProps.minDate, disablePast);
    var placeholder = (_a = restProps.placeholder) !== null && _a !== void 0 ? _a : format.toUpperCase();
    var _g = useDatePickerState(__assign(__assign({}, restProps), { format: format,
        maxDate: maxDate,
        minDate: minDate })), calendarOpen = _g.calendarOpen, setCalendarOpen = _g.setCalendarOpen, changeHandler = _g.changeHandler, date = _g.date, inputValue = _g.inputValue;
    var rootRef = useRef(null);
    var combinedRefs = useForkRef(ref, rootRef);
    var openCalendarButtonRef = useRef(null);
    var readyToShowDatePickerPanel = useReadyToShowDatePickerPanel(initialOpen);
    var rootCompositeFocusProps = useCompositeComponentFocusHandlers({
        onFocus: onFocus,
        onBlur: onBlur,
        name: name,
        blurForNonFocusableElementsWithin: true,
    }).rootCompositeFocusProps;
    var onToggleCalendarHandler = useCallback(function (e) {
        e.stopPropagation();
        setCalendarOpen(function (prevCalendarOpen) { return !prevCalendarOpen; });
    }, []);
    var closeCalendar = useCallback(function (keepFocusInside) {
        var _a;
        if (keepFocusInside) {
            (_a = openCalendarButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        setCalendarOpen(false);
    }, []);
    var onCalendarChangeHandler = useCallback(function (newDate) {
        var newInputValue = newDate && dateFnsIsValid(newDate) ? dateFnsFormat(newDate, format) : '';
        changeHandler({ newDate: newDate, newInputValue: newInputValue });
        closeCalendar(true);
    }, [format, changeHandler, closeCalendar]);
    var onInputChangeHandler = useCallback(function (newInputValue) {
        var newDate = newInputValue !== ''
            ? dateFnsParse(newInputValue, format, new Date())
            : null;
        changeHandler({ newDate: newDate, newInputValue: newInputValue });
    }, [format, changeHandler]);
    var Calendar = (React.createElement(DatePickerCalendar, { date: date, onChange: onCalendarChangeHandler, disableWeekend: disableWeekend, maxDate: maxDate, minDate: minDate, shouldDisableDate: shouldDisableDate, locale: locale, inline: inline }));
    function renderNormal() {
        return (React.createElement(React.Fragment, null,
            React.createElement(DatePickerInput, __assign({}, ariaAttributes, baseInputComponentProps, baseHtmlInputAttributes, { id: inputId, value: inputValue, onChange: onInputChangeHandler, name: name, onClickCalendarIcon: onToggleCalendarHandler, format: format, placeholder: placeholder, autoComplete: autoComplete, openCalendarButtonRef: openCalendarButtonRef })),
            readyToShowDatePickerPanel && (React.createElement(DatePickerPanel, { anchorEl: rootRef.current, open: calendarOpen, onClose: closeCalendar }, Calendar))));
    }
    return (React.createElement(SRoot, __assign({}, rootElementAttributes, dataAttributes, rootCompositeFocusProps, { ref: combinedRefs }), inline ? Calendar : renderNormal()));
});
/**
 * Doing this because when initialOpen is true, the DatePickerPanel is shown,
 * but there is no rootRef set yet.
 */
function useReadyToShowDatePickerPanel(open) {
    var _a = __read(useState(!open), 2), ready = _a[0], setReady = _a[1];
    useEffect(function () {
        setReady(true);
    }, []);
    return ready;
}
//# sourceMappingURL=DatePicker.js.map